import { DISPLAY, GROUND, INK, LABEL_CLS, mix } from "./theme";
import type { NavLink } from "./nav";

export type FooterColumn = { heading: string; links: NavLink[] };

/**
 * Editorial footer — cream ground, hairline top border, wordmark + blurb
 * on the left, link columns on the right, legal row beneath. Calm close
 * after the dark ClosingSection.
 */
export function EditorialFooter({
  wordmark,
  blurb,
  columns,
  legal,
}: {
  wordmark: string;
  blurb?: string;
  columns: FooterColumn[];
  legal: string;
}) {
  return (
    <footer
      className="relative"
      style={{
        backgroundColor: GROUND,
        color: INK,
        borderTop: `1px solid ${mix("--theme-ink", 14)}`,
      }}
    >
      <div className="mx-auto max-w-screen-xl px-5 py-14 sm:px-6 md:px-14 md:py-16">
        <div className="grid gap-10 md:grid-cols-[1.4fr_2fr] md:gap-16">
          <div>
            <span
              style={{ ...DISPLAY, color: INK }}
              className="text-[1.5rem] font-semibold tracking-[-0.02em]"
            >
              {wordmark}
            </span>
            {blurb && (
              <p
                className="mt-4 max-w-[42ch] text-[0.95rem] leading-[1.6]"
                style={{ color: mix("--theme-ink", 88) }}
              >
                {blurb}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3">
            {columns.map((col) => (
              <div key={col.heading}>
                <p
                  className={LABEL_CLS}
                  style={{ color: mix("--theme-ink", 80) }}
                >
                  {col.heading}
                </p>
                <ul className="mt-4 grid gap-2.5">
                  {col.links.map((l) => (
                    <li key={l.href}>
                      <a
                        href={l.href}
                        className="text-[0.95rem] transition-opacity hover:opacity-70"
                        style={{ color: mix("--theme-ink", 88) }}
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div
          className="mt-12 border-t pt-6"
          style={{ borderTopColor: mix("--theme-ink", 12) }}
        >
          <p className="text-[0.82rem]" style={{ color: mix("--theme-ink", 80) }}>
            {legal}
          </p>
        </div>
      </div>
    </footer>
  );
}
